import { type FormEvent, useRef } from 'react';

import { Banner, Flex, Text } from '@spotify-internal/encore-web';
import { Box } from '@spotify-internal/encore-web/next';
import {
  AuthScreen,
  type FormFieldsHandle,
  FormFieldsProvider,
} from '@spotify/confidence-core-react';

import type { SurveyAnswers } from '../../config/onboardingSurvey';
import { CustomRoleFormField } from './CustomRoleFormField';
import { RoleFormField } from './RoleFormField';
import { SurveyActions } from './SurveyActions';
import { type SurveyFormValues, toSurveyAnswers } from './surveyForm';

import styles from './OnboardingSurvey.module.css';

type OnboardingSurveyProps = {
  orgName?: string;
  isSaving: boolean;
  hasSaveError: boolean;
  onSave: (answers: SurveyAnswers) => void;
};

export const OnboardingSurvey = ({
  orgName,
  isSaving,
  hasSaveError,
  onSave,
}: OnboardingSurveyProps) => {
  const formFieldsRef = useRef<FormFieldsHandle<SurveyFormValues>>(null);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = formFieldsRef.current;
    if (!form) {
      return;
    }
    onSave(toSurveyAnswers(form.values));
  };

  return (
    <AuthScreen.Root>
      <AuthScreen.Main>
        <div className={styles.layout}>
          <Flex direction="column" gap="looser" className={styles.welcome}>
            <AuthScreen.Logo />
            <AuthScreen.Title className={styles.title}>
              Before you jump in
            </AuthScreen.Title>
            {orgName && (
              <Text as="p" variant="bodyMedium" semanticColor="textSubdued">
                <Text as="span" semanticColor="textBrightAccent">
                  #{orgName}
                </Text>{' '}
                would like to know a little about you.
              </Text>
            )}
            <Text as="p" variant="bodyMedium" semanticColor="textSubdued">
              Knowing your role helps your workspace understand how you’ll use
              Confidence.
            </Text>
          </Flex>

          <Box
            variant="elevated"
            borderRadius="larger-2"
            padding="looser-2"
            className={styles.card}
          >
            <form onSubmit={handleSubmit} data-track-source-anchor="cbf988c97c">
              <FormFieldsProvider<SurveyFormValues> ref={formFieldsRef}>
                <Flex direction="column" gap="looser">
                  {hasSaveError && (
                    <Banner colorSet="negative" contextual role="alert">
                      Something went wrong. Try again.
                    </Banner>
                  )}
                  <Flex direction="column" gap="tighter">
                    <RoleFormField />
                    <CustomRoleFormField />
                  </Flex>
                  <SurveyActions
                    isSaving={isSaving}
                    onSkip={() => onSave({})}
                  />
                </Flex>
              </FormFieldsProvider>
            </form>
          </Box>
        </div>
      </AuthScreen.Main>
    </AuthScreen.Root>
  );
};
